// AuthContext — Emergent Google OAuth via redirect (web flow).
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "./api";

const AuthCtx = createContext({
  user: null,
  loading: true,
  signIn: () => {},
  signOut: async () => {},
  refresh: async () => {},
});

export const useAuth = () => useContext(AuthCtx);

function extractSessionId(url) {
  try {
    const hashIdx = url.indexOf("#");
    if (hashIdx !== -1) {
      const params = new URLSearchParams(url.slice(hashIdx + 1));
      const sid = params.get("session_id");
      if (sid) return sid;
    }
    const qIdx = url.indexOf("?");
    if (qIdx !== -1) {
      const params = new URLSearchParams(url.slice(qIdx + 1));
      const sid = params.get("session_id");
      if (sid) return sid;
    }
  } catch (e) {}
  return null;
}

async function processSessionId(sessionId) {
  try {
    const r = await api.post("/auth/session", { session_id: sessionId });
    localStorage.setItem(TOKEN_KEY, r.session_token);
    return r.user;
  } catch (e) {
    console.error("[auth] session exchange failed:", e?.message);
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me);
    } catch {
      setUser(null);
      localStorage.removeItem(TOKEN_KEY);
    }
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const sid = extractSessionId(window.location.href);
        if (sid) {
          const u = await processSessionId(sid);
          if (u && !cancel) setUser(u);
          // clean URL
          try {
            window.history.replaceState(null, "", window.location.pathname);
          } catch {}
        } else {
          await refresh();
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(() => {
    const redirect = window.location.origin + "/";
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    window.location.href = authUrl;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, signIn, signOut, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}
